import { loyaltyLaneClient } from '@/services/api/loyalty-lane-client';
import { TokenStorage } from '../lib/token-storage';
import type {
  LLApiResponse,
  LLCoupon,
  AvailableCoupon,
  ClippedCoupon,
  ClipCouponRequest,
} from '../types/loyalty-lane';
import type { Coupon, CouponsResponse } from '../types/coupons';

// ---------------------------------------------------------------------------
// Normalization: convert LL coupon shapes → app Coupon type
// ---------------------------------------------------------------------------

function normalizeLLCoupon(raw: LLCoupon, isClipped = false): Coupon {
  // API returns lowercase 'id', type definition has uppercase 'ID'
  const couponId = (raw as Record<string, unknown>).id ?? raw.ID;
  // Value can be a number or string from the API
  const value = raw.Value != null ? String(raw.Value) : '';
  return {
    id: String(couponId),
    offerId: String(couponId),
    title: raw.OfferSummaryTop || value || '',
    subtitle: raw.Brand || '',
    brand: raw.Brand || '',
    description: raw.OfferShortDesc || '',
    imageUrl: raw.ImageUrl || '',
    category: raw.CategoryName || '',
    expirationDate: raw.ExpirationDate || raw.ShutOffDate || '',
    value,
    isClipped,
  };
}

function normalizeAvailableCoupon(raw: AvailableCoupon): Coupon {
  return {
    ...normalizeLLCoupon(raw, raw.IsClipped),
    isClipped: raw.IsClipped,
  };
}

function normalizeClippedCoupon(raw: ClippedCoupon): Coupon {
  return normalizeLLCoupon(raw, true);
}

// ---------------------------------------------------------------------------
// API functions (Loyalty Lane)
// ---------------------------------------------------------------------------

/**
 * Fetches all coupons (unauthenticated — CST only).
 * Uses GET api/Coupon with optional StoreId filtering.
 */
export async function getCoupons(
  storeId?: string | null,
): Promise<CouponsResponse> {
  const params: Record<string, string | number | boolean> = {};
  if (storeId) {
    params.StoreId = storeId;
  }

  const response = await loyaltyLaneClient.get<LLApiResponse<{ coupons: LLCoupon[] }>>(
    'Coupon',
    { params },
  );

  const data = response.data;
  const coupons = (data.data?.coupons ?? []).map((c) => normalizeLLCoupon(c));
  return { items: coupons };
}

/**
 * Fetches personalized available coupons (authenticated — SST required).
 * Uses GET api/AvailableCoupon with accountid and optional StoreId.
 */
export async function getAvailableCoupons(
  storeId?: string | null,
): Promise<CouponsResponse> {
  const accountId = TokenStorage.getAccountId();
  const params: Record<string, string | number | boolean> = {};
  if (accountId) {
    params.accountid = accountId;
  }
  if (storeId) {
    params.StoreId = storeId;
  }

  const response = await loyaltyLaneClient.get<LLApiResponse<{ coupons: AvailableCoupon[] }>>(
    'AvailableCoupon',
    { params },
  );

  const data = response.data;
  const coupons = (data.data?.coupons ?? []).map((c) => normalizeAvailableCoupon(c));
  return { items: coupons };
}

/**
 * Fetches clipped coupons for the authenticated user.
 * Uses GET api/ClippedCoupon.
 * Note: LL API can return duplicates — we deduplicate by coupon ID.
 */
export async function getClippedCoupons(): Promise<CouponsResponse> {
  const response = await loyaltyLaneClient.get<LLApiResponse<{ coupons: ClippedCoupon[] }>>(
    'ClippedCoupon',
  );

  const data = response.data;
  const raw = data.data?.coupons ?? [];

  // Deduplicate — LL sometimes returns multiple copies of the same clipped coupon
  const seen = new Set<string>();
  const unique = raw.filter((c) => {
    const id = String((c as Record<string, unknown>).id ?? c.ID);
    if (seen.has(id)) return false;
    seen.add(id);
    return true;
  });

  const coupons = unique.map((c) => normalizeClippedCoupon(c));
  return { items: coupons };
}

/**
 * Clips one or more coupons for the authenticated user.
 * Uses POST api/ClippedCoupon.
 */
export async function clipCoupon(couponId: string): Promise<void> {
  const accountId = TokenStorage.getAccountId();
  const body: ClipCouponRequest = {
    couponidlist: [Number(couponId)],
  };
  if (accountId) {
    body.accountid = accountId;
  }

  await loyaltyLaneClient.post<LLApiResponse<unknown>>(
    'ClippedCoupon',
    body,
  );
}

/**
 * Checks if user is authenticated (has valid SST).
 */
export function isAuthenticated(): boolean {
  return TokenStorage.isAuthenticated();
}
